import React, { useCallback, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Server, Building2, Plug, Key, CheckCircle2, Shield, Eye, EyeOff, AlertTriangle, Copy, Check, Terminal } from 'lucide-react';
import { OAuthPlatform, OAuthEnvironment, TokenScope, ConnectorSetupState, OAUTH_CONNECTORS, ENVIRONMENT_URLS, saveSetupState, loadSetupState, getShortScopeLabel } from '../../services/oauthConnectors';
interface OAuthSetupWizardProps {
  open: boolean;
  onClose: () => void;
}
type WizardStep = 'environment' | 'tenant' | 'connectors' | 'credentials' | 'review';
const STEPS: {
  id: WizardStep;
  label: string;
  title: string;
  description: string;
  icon: React.ElementType;
}[] = [{
  id: 'environment',
  label: 'Entorno',
  title: 'Entorno de Despliegue',
  description: 'Seleccioná dónde se ejecutará el flujo OAuth',
  icon: Server
}, {
  id: 'tenant',
  label: 'Tenant',
  title: 'Configuración del Tenant',
  description: 'Identificación y alcance de tokens',
  icon: Building2
}, {
  id: 'connectors',
  label: 'Conectores',
  title: 'Selección de Conectores',
  description: 'Elegí las plataformas y personalizá scopes',
  icon: Plug
}, {
  id: 'credentials',
  label: 'Credenciales',
  title: 'Credenciales OAuth',
  description: 'Client ID y Secret de cada plataforma',
  icon: Key
}, {
  id: 'review',
  label: 'Revisión',
  title: 'Revisión Final',
  description: 'Verificá la configuración antes de guardar',
  icon: CheckCircle2
}];
const PLATFORM_LABELS: Record<OAuthPlatform, string> = {
  jira: 'Jira',
  confluence: 'Confluence',
  notion: 'Notion',
  slack: 'Slack',
  github: 'GitHub',
  google: 'Google Workspace'
};
function getDefaultState(): ConnectorSetupState {
  return {
    environment: 'production',
    redirectBase: ENVIRONMENT_URLS.production + '/integrations/callback',
    tenantId: '',
    tokenScope: 'per-user',
    selectedConnectors: [],
    connectorScopes: {},
    credentials: {}
  };
}
export function OAuthSetupWizard({
  open,
  onClose
}: OAuthSetupWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('environment');
  const [state, setState] = useState<ConnectorSetupState>(() => {
    return loadSetupState() || getDefaultState();
  });
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeCredentialTab, setActiveCredentialTab] = useState<OAuthPlatform | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const stepIndex = STEPS.findIndex(s => s.id === currentStep);
  const currentStepData = STEPS[stepIndex];
  const updateState = useCallback((updates: Partial<ConnectorSetupState>) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const goNext = () => {
    const nextIdx = Math.min(stepIndex + 1, STEPS.length - 1);
    setCurrentStep(STEPS[nextIdx].id);
  };
  const goBack = () => {
    const prevIdx = Math.max(stepIndex - 1, 0);
    setCurrentStep(STEPS[prevIdx].id);
  };
  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'environment':
        return !!state.environment;
      case 'tenant':
        return state.tenantId.trim().length > 0;
      case 'connectors':
        return state.selectedConnectors.length > 0;
      case 'credentials':
        return state.selectedConnectors.every(p => {
          const cred = state.credentials[p];
          return cred?.clientId?.trim();
        });
      case 'review':
        return true;
      default:
        return false;
    }
  };
  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    saveSetupState(state);
    setSaving(false);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 1500);
  };
  const copyToClipboard = (text: string, fieldId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };
  const toggleConnector = (platform: OAuthPlatform) => {
    setState(prev => {
      const selected = prev.selectedConnectors.includes(platform) ? prev.selectedConnectors.filter(p => p !== platform) : [...prev.selectedConnectors, platform];
      const scopes = {
        ...prev.connectorScopes
      };
      if (!selected.includes(platform)) {
        delete scopes[platform];
      } else if (!scopes[platform]) {
        const config = OAUTH_CONNECTORS.find(c => c.id === platform);
        scopes[platform] = config?.defaultScopes || [];
      }
      return {
        ...prev,
        selectedConnectors: selected,
        connectorScopes: scopes
      };
    });
  };
  const toggleScope = (platform: OAuthPlatform, scope: string) => {
    setState(prev => {
      const current = prev.connectorScopes[platform] || [];
      const updated = current.includes(scope) ? current.filter(s => s !== scope) : [...current, scope];
      return {
        ...prev,
        connectorScopes: {
          ...prev.connectorScopes,
          [platform]: updated
        }
      };
    });
  };
  const updateCredential = (platform: OAuthPlatform, field: 'clientId' | 'clientSecret', value: string) => {
    setState(prev => {
      const existing = prev.credentials[platform] || {
        clientId: '',
        clientSecret: '',
        scopes: prev.connectorScopes[platform] || [],
        configuredAt: ''
      };
      return {
        ...prev,
        credentials: {
          ...prev.credentials,
          [platform]: {
            ...existing,
            [field]: value,
            scopes: prev.connectorScopes[platform] || [],
            configuredAt: new Date().toISOString()
          }
        }
      };
    });
  };
  // ─── Step Renderers ─────────────────────────────────────────────────
  const renderEnvironmentStep = () => <div className="space-y-6" data-id="element-5522">
      <div data-id="element-5523">
        <div className="space-y-3" data-id="element-5524">
          {(['local', 'staging', 'production'] as OAuthEnvironment[]).map(env => {
          const labels: Record<OAuthEnvironment, {
            name: string;
            desc: string;
          }> = {
            local: {
              name: 'Local',
              desc: 'http://localhost:3000'
            },
            staging: {
              name: 'Staging',
              desc: 'https://staging.workium.app'
            },
            production: {
              name: 'Production',
              desc: 'https://app.workium.com'
            }
          };
          const isSelected = state.environment === env;
          return <button key={env} onClick={() => updateState({
            environment: env,
            redirectBase: ENVIRONMENT_URLS[env] + '/integrations/callback'
          })} className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${isSelected ? 'bg-indigo-950/20 border-indigo-500/50 ring-1 ring-indigo-500/30' : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`} data-id="element-5525">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-indigo-500' : 'border-slate-600'}`} data-id="element-5526">
                    {isSelected && <div className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-5527" />}
                  </div>
                  <div className="flex-1" data-id="element-5528">
                    <p className={`text-sm font-semibold ${isSelected ? 'text-indigo-300' : 'text-slate-300'}`} data-id="element-5529">
                      {labels[env].name}
                    </p>
                    <p className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-5530">
                      {labels[env].desc}
                    </p>
                  </div>
                  {env === 'production' && <span className="text-[10px] px-2 py-1 bg-emerald-950/40 text-emerald-400 border border-emerald-900/40 rounded-full font-bold uppercase tracking-wider" data-id="element-5531">
                      Recomendado
                    </span>}
                </button>;
        })}
        </div>
      </div>

      <div className="pt-4 border-t border-slate-800/50" data-id="element-5532">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 block" data-id="element-5533">
          Redirect URI Generada
        </label>
        <div className="flex items-center gap-3 bg-slate-950 border border-slate-800 rounded-xl p-4" data-id="element-5534">
          <Terminal size={16} className="text-slate-500 shrink-0" data-id="element-5535" />
          <code className="text-sm text-indigo-300 font-mono flex-1 break-all" data-id="element-5536">
            {state.redirectBase}/{'{platform}'}
          </code>
          <button onClick={() => copyToClipboard(state.redirectBase, 'redirect')} className="p-2 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-slate-200 transition-colors" title="Copiar base URI" data-id="element-5537">
            {copiedField === 'redirect' ? <Check size={16} className="text-emerald-400" data-id="element-5538" /> : <Copy size={16} data-id="element-5539" />}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3 flex items-start gap-2" data-id="element-5540">
          <Shield size={14} className="text-slate-400 shrink-0 mt-0.5" data-id="element-5541" />
          <span data-id="element-5542">
            Configurá esta URL base como <strong data-id="element-5543">redirect_uri</strong> en cada
            OAuth App de cada plataforma. Reemplazá {'{platform}'} por el ID
            correspondiente (ej: jira, github).
          </span>
        </p>
      </div>
    </div>;
  const renderTenantStep = () => <div className="space-y-8" data-id="element-5544">
      <div data-id="element-5545">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 block" data-id="element-5546">
          Tenant ID
        </label>
        <input type="text" value={state.tenantId} onChange={e => updateState({
        tenantId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')
      })} placeholder="ej: acme-corp" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 font-mono placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-all" data-id="element-5547" />
        <p className="text-xs text-slate-500 mt-2" data-id="element-5548">
          El tenant_id identifica a tu organización. Solo letras minúsculas,
          números, guiones y guiones bajos.
        </p>
      </div>

      <div className="pt-6 border-t border-slate-800/50" data-id="element-5549">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 block" data-id="element-5550">
          Alcance de tokens (Scope)
        </label>
        <div className="space-y-3" data-id="element-5551">
          {[{
          value: 'per-user' as TokenScope,
          label: 'Por usuario',
          desc: 'Cada usuario autoriza con sus propios permisos. Recomendado para máxima seguridad y trazabilidad.',
          path: `integrations/{'{platform}'}/${state.tenantId || '{tenant_id}'}/{'{user_id}'}`,
          recommended: true
        }, {
          value: 'per-tenant' as TokenScope,
          label: 'Por tenant',
          desc: 'Un token compartido para toda la organización. Útil para bots o integraciones globales.',
          path: `integrations/{'{platform}'}/${state.tenantId || '{tenant_id}'}`,
          recommended: false
        }].map(option => {
          const isSelected = state.tokenScope === option.value;
          return <button key={option.value} onClick={() => updateState({
            tokenScope: option.value
          })} className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all text-left ${isSelected ? 'bg-indigo-950/20 border-indigo-500/50 ring-1 ring-indigo-500/30' : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`} data-id="element-5552">
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-indigo-500' : 'border-slate-600'}`} data-id="element-5553">
                  {isSelected && <div className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-5554" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-5555">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5556">
                    <p className={`text-sm font-semibold ${isSelected ? 'text-indigo-300' : 'text-slate-300'}`} data-id="element-5557">
                      {option.label}
                    </p>
                    {option.recommended && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-950/40 text-emerald-400 border border-emerald-900/40 rounded-full font-bold uppercase tracking-wider" data-id="element-5558">
                        Recomendado
                      </span>}
                  </div>
                  <p className="text-xs text-slate-500 mb-3" data-id="element-5559">{option.desc}</p>
                  <div className="bg-slate-950 border border-slate-800/50 rounded-lg p-2.5" data-id="element-5560">
                    <p className="text-[10px] text-slate-600 uppercase font-semibold mb-1" data-id="element-5561">
                      Storage Path
                    </p>
                    <code className="text-[11px] text-slate-400 font-mono block break-all" data-id="element-5562">
                      {option.path}
                    </code>
                  </div>
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
  const renderConnectorsStep = () => <div className="space-y-4" data-id="element-5563">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5564">
        {OAUTH_CONNECTORS.map(connector => {
        const isSelected = state.selectedConnectors.includes(connector.id);
        const scopes = state.connectorScopes[connector.id] || connector.defaultScopes;
        const allScopes = Object.keys(connector.scopeDescriptions);
        return <div key={connector.id} className={`rounded-xl border transition-all overflow-hidden flex flex-col ${isSelected ? 'bg-slate-900/80 border-slate-700 ring-1 ring-slate-700/50' : 'bg-slate-800/30 border-slate-800/50'}`} data-id="element-5565">
              <button onClick={() => toggleConnector(connector.id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-800/20 transition-colors" data-id="element-5566">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-600'}`} data-id="element-5567">
                  {isSelected && <Check size={12} className="text-white" data-id="element-5568" />}
                </div>
                <div className="w-1.5 h-8 rounded-full shrink-0" style={{
              backgroundColor: connector.brandColor
            }} data-id="element-5569" />
                <div className="flex-1 min-w-0" data-id="element-5570">
                  <p className={`text-sm font-bold truncate ${isSelected ? 'text-slate-200' : 'text-slate-400'}`} data-id="element-5571">
                    {connector.name}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-5572">
                    {scopes.length} scope{scopes.length !== 1 ? 's' : ''}{' '}
                    seleccionado{scopes.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </button>

              {isSelected && allScopes.length > 0 && <div className="px-4 pb-4 pt-2 border-t border-slate-800/50 bg-slate-900/50 flex-1" data-id="element-5573">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3" data-id="element-5574">
                    Personalizar Scopes
                  </p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar pr-2" data-id="element-5575">
                    {allScopes.map(scope => {
                const isActive = scopes.includes(scope);
                const isDefault = connector.defaultScopes.includes(scope);
                return <button key={scope} onClick={() => toggleScope(connector.id, scope)} className={`w-full flex items-start gap-3 p-2.5 rounded-lg text-left transition-colors border ${isActive ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-950/50 border-slate-800/50 hover:border-slate-700'}`} data-id="element-5576">
                          <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isActive ? 'bg-indigo-600 border-indigo-600' : 'border-slate-600'}`} data-id="element-5577">
                            {isActive && <Check size={10} className="text-white" data-id="element-5578" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5579">
                            <div className="flex items-center gap-2 mb-1" data-id="element-5580">
                              <code className={`text-[11px] font-mono truncate ${isActive ? 'text-slate-200' : 'text-slate-500'}`} title={scope} data-id="element-5581">
                                {getShortScopeLabel(scope)}
                              </code>
                              {isDefault && <span className="text-[9px] px-1.5 py-0.5 bg-slate-800 text-slate-400 rounded uppercase font-bold shrink-0" data-id="element-5582">
                                  default
                                </span>}
                            </div>
                            <p className="text-[10px] text-slate-500 leading-snug" data-id="element-5583">
                              {connector.scopeDescriptions[scope]}
                            </p>
                          </div>
                        </button>;
              })}
                  </div>
                </div>}
            </div>;
      })}
      </div>
    </div>;
  const renderCredentialsStep = () => {
    const activeTab = activeCredentialTab || state.selectedConnectors[0];
    if (state.selectedConnectors.length === 0) {
      return <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-5584">
          <div className="w-16 h-16 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center mb-4" data-id="element-5585">
            <Plug size={24} className="text-slate-500" data-id="element-5586" />
          </div>
          <h3 className="text-lg font-semibold text-slate-300 mb-2" data-id="element-5587">
            Ningún conector seleccionado
          </h3>
          <p className="text-sm text-slate-500 max-w-sm" data-id="element-5588">
            Volvé al paso anterior y seleccioná al menos una plataforma para
            configurar sus credenciales.
          </p>
          <button onClick={goBack} className="mt-6 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-5589">
            Volver a Conectores
          </button>
        </div>;
    }
    return <div className="space-y-6" data-id="element-5590">
        <div className="flex items-center gap-3 p-3 bg-amber-950/20 border border-amber-900/30 rounded-xl" data-id="element-5591">
          <Shield size={18} className="text-amber-400 shrink-0" data-id="element-5592" />
          <p className="text-xs text-amber-300/90 leading-relaxed" data-id="element-5593">
            <strong data-id="element-5594">Seguridad:</strong> Los secrets ingresados aquí nunca se
            almacenan en el frontend ni en localStorage. Se envían directamente
            al backend para el intercambio seguro de tokens.
          </p>
        </div>

        {/* Connector tabs */}
        <div className="flex flex-wrap gap-2 bg-slate-900/50 p-1.5 rounded-xl border border-slate-800" data-id="element-5595">
          {state.selectedConnectors.map(platform => {
          const config = OAUTH_CONNECTORS.find(c => c.id === platform);
          const isActive = activeTab === platform;
          const hasCreds = !!state.credentials[platform]?.clientId && !!state.credentials[platform]?.clientSecret;
          return <button key={platform} onClick={() => setActiveCredentialTab(platform)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-id="element-5596">
                <div className="w-2 h-2 rounded-full shrink-0" style={{
              backgroundColor: config?.brandColor
            }} data-id="element-5597" />
                {PLATFORM_LABELS[platform]}
                {hasCreds && <CheckCircle2 size={14} className="text-emerald-400 ml-1" data-id="element-5598" />}
              </button>;
        })}
        </div>

        {/* Active connector form */}
        {activeTab && <motion.div key={activeTab} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2
      }} className="bg-slate-800/20 border border-slate-700/50 rounded-xl p-6 space-y-6" data-id="element-5599">
            <div className="flex items-center gap-3 mb-2" data-id="element-5600">
              <div className="w-1.5 h-6 rounded-full shrink-0" style={{
            backgroundColor: OAUTH_CONNECTORS.find(c => c.id === activeTab)?.brandColor
          }} data-id="element-5601" />
              <h4 className="text-lg font-bold text-slate-200" data-id="element-5602">
                Credenciales de {PLATFORM_LABELS[activeTab]}
              </h4>
            </div>

            <div className="space-y-5" data-id="element-5603">
              <div data-id="element-5604">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block" data-id="element-5605">
                  Client ID
                </label>
                <input type="text" value={state.credentials[activeTab]?.clientId || ''} onChange={e => updateCredential(activeTab, 'clientId', e.target.value)} placeholder="Ej: 1234567890-abcde.apps.googleusercontent.com" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-200 font-mono placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-all" data-id="element-5606" />
              </div>

              <div data-id="element-5607">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block" data-id="element-5608">
                  Client Secret
                </label>
                <div className="relative" data-id="element-5609">
                  <input type={showSecrets[activeTab] ? 'text' : 'password'} value={state.credentials[activeTab]?.clientSecret || ''} onChange={e => updateCredential(activeTab, 'clientSecret', e.target.value)} placeholder="••••••••••••••••••••••••••••••••" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 pr-12 text-sm text-slate-200 font-mono placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-all" data-id="element-5610" />
                  <button type="button" onClick={() => setShowSecrets(prev => ({
                ...prev,
                [activeTab]: !prev[activeTab]
              }))} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors p-1" data-id="element-5611">
                    {showSecrets[activeTab] ? <EyeOff size={18} data-id="element-5612" /> : <Eye size={18} data-id="element-5613" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-700/50" data-id="element-5614">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5615">
                Scopes a solicitar
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-5616">
                {(state.connectorScopes[activeTab] || []).length > 0 ? (state.connectorScopes[activeTab] || []).map(scope => <span key={scope} className="px-2.5 py-1 bg-slate-900 text-slate-300 text-xs font-mono rounded-md border border-slate-700" data-id="element-5617">
                      {getShortScopeLabel(scope)}
                    </span>) : <span className="text-sm text-slate-500 italic" data-id="element-5618">
                    No se seleccionaron scopes explícitos.
                  </span>}
              </div>
            </div>
          </motion.div>}
      </div>;
  };
  const renderReviewStep = () => <div className="space-y-6" data-id="element-5619">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5620">
        {/* Environment */}
        <div className="p-5 bg-slate-800/30 border border-slate-700/50 rounded-xl flex flex-col" data-id="element-5621">
          <div className="flex items-center gap-2 mb-3" data-id="element-5622">
            <Server size={16} className="text-slate-400" data-id="element-5623" />
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5624">
              Entorno
            </p>
          </div>
          <p className="text-lg text-slate-200 font-bold capitalize mb-1" data-id="element-5625">
            {state.environment}
          </p>
          <code className="text-xs text-indigo-400 font-mono mt-auto pt-3 border-t border-slate-700/50 break-all" data-id="element-5626">
            {ENVIRONMENT_URLS[state.environment]}
          </code>
        </div>

        {/* Tenant */}
        <div className="p-5 bg-slate-800/30 border border-slate-700/50 rounded-xl flex flex-col" data-id="element-5627">
          <div className="flex items-center gap-2 mb-3" data-id="element-5628">
            <Building2 size={16} className="text-slate-400" data-id="element-5629" />
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5630">
              Tenant & Scope
            </p>
          </div>
          <p className="text-lg text-slate-200 font-mono font-bold mb-1" data-id="element-5631">
            {state.tenantId || <span className="text-slate-500 italic" data-id="element-5632">No definido</span>}
          </p>
          <div className="mt-auto pt-3 border-t border-slate-700/50 flex items-center gap-2" data-id="element-5633">
            <span className="text-xs text-slate-400" data-id="element-5634">Tokens:</span>
            <span className="text-xs font-medium text-slate-200 bg-slate-800 px-2 py-0.5 rounded" data-id="element-5635">
              {state.tokenScope === 'per-user' ? 'Por usuario' : 'Por tenant'}
            </span>
          </div>
        </div>
      </div>

      {/* Connectors Summary */}
      <div className="p-5 bg-slate-800/30 border border-slate-700/50 rounded-xl" data-id="element-5636">
        <div className="flex items-center justify-between mb-4" data-id="element-5637">
          <div className="flex items-center gap-2" data-id="element-5638">
            <Plug size={16} className="text-slate-400" data-id="element-5639" />
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5640">
              Conectores Configurados ({state.selectedConnectors.length})
            </p>
          </div>
        </div>

        {state.selectedConnectors.length === 0 ? <p className="text-sm text-slate-500 italic py-4 text-center" data-id="element-5641">
            No se seleccionaron conectores.
          </p> : <div className="space-y-3" data-id="element-5642">
            {state.selectedConnectors.map(platform => {
          const config = OAUTH_CONNECTORS.find(c => c.id === platform);
          const scopes = state.connectorScopes[platform] || [];
          const hasCreds = !!state.credentials[platform]?.clientId && !!state.credentials[platform]?.clientSecret;
          return <div key={platform} className="flex items-start gap-4 p-3 bg-slate-900/50 rounded-lg border border-slate-800" data-id="element-5643">
                  <div className="w-1.5 h-10 rounded-full shrink-0" style={{
              backgroundColor: config?.brandColor
            }} data-id="element-5644" />
                  <div className="flex-1 min-w-0" data-id="element-5645">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-5646">
                      <p className="text-sm font-bold text-slate-200" data-id="element-5647">
                        {PLATFORM_LABELS[platform]}
                      </p>
                      {hasCreds ? <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-400 bg-emerald-950/30 px-2 py-0.5 rounded-full border border-emerald-900/30" data-id="element-5648">
                          <CheckCircle2 size={10} data-id="element-5649" /> Credenciales OK
                        </span> : <span className="flex items-center gap-1 text-[10px] font-medium text-amber-400 bg-amber-950/30 px-2 py-0.5 rounded-full border border-amber-900/30" data-id="element-5650">
                          <AlertTriangle size={10} data-id="element-5651" /> Faltan credenciales
                        </span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-id="element-5652">
                      {scopes.length > 0 ? scopes.map(scope => <span key={scope} className="px-1.5 py-0.5 bg-slate-800 text-slate-400 text-[9px] font-mono rounded" data-id="element-5653">
                            {getShortScopeLabel(scope)}
                          </span>) : <span className="text-[10px] text-slate-500 italic" data-id="element-5654">
                          Sin scopes explícitos
                        </span>}
                    </div>
                  </div>
                </div>;
        })}
          </div>}
      </div>

      {saved && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex items-center justify-center gap-3 p-4 bg-emerald-950/30 border border-emerald-900/40 rounded-xl" data-id="element-5655">
          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-5656">
            <CheckCircle2 size={18} className="text-emerald-400" data-id="element-5657" />
          </div>
          <p className="text-sm text-emerald-300 font-medium" data-id="element-5658">
            Configuración guardada exitosamente. Ya podés conectar las
            integraciones.
          </p>
        </motion.div>}
    </div>;
  const renderStepContent = () => {
    switch (currentStep) {
      case 'environment':
        return renderEnvironmentStep();
      case 'tenant':
        return renderTenantStep();
      case 'connectors':
        return renderConnectorsStep();
      case 'credentials':
        return renderCredentialsStep();
      case 'review':
        return renderReviewStep();
    }
  };
  return <AnimatePresence data-id="element-5659">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 md:p-8" data-id="element-5660">
            {/* Modal Container */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} className="w-full max-w-5xl max-h-[90vh] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex overflow-hidden" data-id="element-5661">
              {/* Left Sidebar: Vertical Stepper */}
              <div className="w-64 bg-slate-950 border-r border-slate-800 flex flex-col shrink-0 hidden md:flex" data-id="element-5662">
                <div className="p-6 border-b border-slate-800/50" data-id="element-5663">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5664">
                    <Key size={20} className="text-indigo-400" data-id="element-5665" />
                    <h2 className="text-lg font-bold text-slate-100" data-id="element-5666">
                      OAuth Setup
                    </h2>
                  </div>
                  <span className="inline-block text-[10px] px-2 py-0.5 bg-indigo-950/50 text-indigo-400 border border-indigo-900/50 rounded-full font-bold uppercase tracking-wider" data-id="element-5667">
                    Admin Configuration
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto py-6 px-4" data-id="element-5668">
                  <div className="space-y-1 relative" data-id="element-5669">
                    {/* Vertical connecting line */}
                    <div className="absolute left-[23px] top-6 bottom-6 w-px bg-slate-800 z-0" data-id="element-5670" />

                    {STEPS.map((step, idx) => {
                  const isActive = idx === stepIndex;
                  const isCompleted = idx < stepIndex;
                  const StepIcon = step.icon;
                  return <button key={step.id} onClick={() => idx <= stepIndex && setCurrentStep(step.id)} disabled={idx > stepIndex} className={`relative z-10 w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all ${isActive ? 'bg-indigo-950/30' : isCompleted ? 'hover:bg-slate-900 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`} data-id="element-5671">
                          {/* Active indicator line */}
                          {isActive && <motion.div layoutId="activeStepIndicator" className="absolute left-0 top-2 bottom-2 w-1 bg-indigo-500 rounded-r-full" transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 30
                    }} data-id="element-5672" />}

                          <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${isActive ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' : isCompleted ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`} data-id="element-5673">
                            {isCompleted ? <Check size={12} strokeWidth={3} data-id="element-5674" /> : <StepIcon size={12} data-id="element-5675" />}
                          </div>

                          <div data-id="element-5676">
                            <p className={`text-sm font-bold ${isActive ? 'text-indigo-300' : isCompleted ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-5677">
                              {step.label}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-0.5 leading-tight" data-id="element-5678">
                              {step.description}
                            </p>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                <div className="p-4 border-t border-slate-800/50 bg-slate-900/20" data-id="element-5679">
                  <div className="flex items-start gap-2 text-[10px] text-slate-500" data-id="element-5680">
                    <Shield size={14} className="shrink-0 text-slate-600" data-id="element-5681" />
                    <p data-id="element-5682">
                      Configuración segura a nivel de tenant. Los secrets no se
                      exponen al cliente.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Main Content Area */}
              <div className="flex-1 flex flex-col min-w-0 bg-slate-900" data-id="element-5683">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-800 flex items-start justify-between shrink-0 bg-slate-900/50" data-id="element-5684">
                  <div data-id="element-5685">
                    <h2 className="text-2xl font-bold text-slate-100 mb-1" data-id="element-5686">
                      {currentStepData.title}
                    </h2>
                    <p className="text-sm text-slate-400" data-id="element-5687">
                      {currentStepData.description}
                    </p>
                  </div>
                  <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" data-id="element-5688">
                    <X size={20} data-id="element-5689" />
                  </button>
                </div>

                {/* Mobile step indicator (only visible on small screens) */}
                <div className="md:hidden px-6 py-3 border-b border-slate-800 bg-slate-950 flex items-center justify-between" data-id="element-5690">
                  <span className="text-xs font-medium text-slate-400" data-id="element-5691">
                    Paso {stepIndex + 1} de {STEPS.length}:{' '}
                    <strong className="text-slate-200" data-id="element-5692">
                      {currentStepData.label}
                    </strong>
                  </span>
                  <div className="flex gap-1" data-id="element-5693">
                    {STEPS.map((_, idx) => <div key={idx} className={`w-2 h-2 rounded-full ${idx === stepIndex ? 'bg-indigo-500' : idx < stepIndex ? 'bg-emerald-500' : 'bg-slate-800'}`} data-id="element-5694" />)}
                  </div>
                </div>

                {/* Content Scroll Area */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar" data-id="element-5695">
                  <AnimatePresence mode="wait" data-id="element-5696">
                    <motion.div key={currentStep} initial={{
                  opacity: 0,
                  x: 15
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -15
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="max-w-3xl mx-auto w-full" data-id="element-5697">
                      {renderStepContent()}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Footer Actions */}
                <div className="px-8 py-5 border-t border-slate-800 bg-slate-900/80 shrink-0 flex items-center justify-between" data-id="element-5698">
                  <button onClick={goBack} disabled={stepIndex === 0} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-xl disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all" data-id="element-5699">
                    <ChevronLeft size={16} data-id="element-5700" />
                    Anterior
                  </button>

                  {currentStep === 'review' ? <button onClick={handleSave} disabled={saving || saved} className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white text-sm font-bold rounded-xl shadow-lg shadow-emerald-900/20 transition-all" data-id="element-5701">
                      {saving ? <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-5702" />
                          Guardando...
                        </> : saved ? <>
                          <CheckCircle2 size={16} data-id="element-5703" />
                          Guardado
                        </> : <>
                          <CheckCircle2 size={16} data-id="element-5704" />
                          Guardar Configuración
                        </>}
                    </button> : <button onClick={goNext} disabled={!canProceed()} className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-900/20 transition-all" data-id="element-5705">
                      Siguiente
                      <ChevronRight size={16} data-id="element-5706" />
                    </button>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}